/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { dropTask, restartableTask } from 'ember-concurrency';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { FlyMouseEvent } from '@customerio/fly-components/types/events';
import rangeSelectionHelper from 'cio/utils/range-selection-helper';
import DS from 'ember-data';
import RelationshipModel from 'cio/models/relationship';
import ApiService from 'cio/services/api';
import config from 'cio/config/environment';
import Component from '@glimmer/component';
import ObjectModel from 'cio/models/object';
import CustomerModel from 'cio/models/customer';
import AnalyticsService from 'cio/services/analytics';
import AlertsService from 'cio/services/alerts';
import SnackbarService from 'cio/services/snackbar';
import SidebarService, { SidebarId, StaticSidebarId } from 'cio/services/sidebar';
import AttributesProxy from 'cio/app-models/customer/attributes-proxy';
import ignorePromiseFailure from 'cio/utils/ignore-promise-failure';
import SettingsService from 'cio/services/settings';
import FeaturesService from 'ember-feature-flags/services/features';
import RelationshipsService from 'cio/services/relationships';
import { registerDestructor } from '@ember/destroyable';
import ObjectTypesService from 'cio/services/object-types';

interface Args {
  objectTypeId: string;
  object?: ObjectModel;
  customer?: CustomerModel;
  showAddButton: boolean;
  source: string;
  itemsPerPage?: number;
  isCardView?: boolean;
}

export default class RelationshipsTableComponent extends Component<Args> {
  @service store!: DS.Store;
  @service api!: ApiService;
  @service analytics!: AnalyticsService;
  @service snackbar!: SnackbarService;
  @service alerts!: AlertsService;
  @service sidebar!: SidebarService;
  @service settings!: SettingsService;
  @service features!: FeaturesService;
  @service('objectTypes') objectTypesService!: ObjectTypesService;
  @service('relationships') relationshipsService!: RelationshipsService;

  @tracked allSelected = false;
  @tracked selectedRelationships: RelationshipModel[] = [];

  @tracked shiftSelectStart: number | null = null;
  @tracked shiftSelectEnd: number | null = null;

  @tracked showRelationshipsModal = false;
  @tracked showColumnsModal = false;
  @tracked showAttributeModal = false;
  @tracked relationshipForModal: RelationshipModel | null = null;
  @tracked attributesProxy: AttributesProxy | null = null;
  @tracked editingAttributesForm = false;

  readonly sidebarId: SidebarId = `${StaticSidebarId.RelationshipsTable}-${this.args.objectTypeId}`;
  readonly isAttributesEnabled = this.features.isEnabled('campaign_trigger_ui_new');

  @tracked totalRecs = -1;
  @tracked page = 1;
  itemsPerPage = this.args.itemsPerPage ?? 5;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.fetchRelationships.perform();

    registerDestructor(this, () => {
      this.hideAttributeSidebar();
    });

    this.settings.cleanupAttributesFromSettings(
      'relationshipColumns',
      this.objectType?.sentRelAttributes,
      this.args.objectTypeId,
    );
  }

  get relationshipTableColumns() {
    return this.settings.environmentSettings.relationshipColumns?.[this.args.objectTypeId] ?? [];
  }

  get extraAttributes() {
    let { attrs = {} } = this.attributesProxy?.getModelAttributes() ?? {};
    let extraAttributes = [...(this.objectType?.sentRelAttributes ?? [])];

    for (let key in attrs) {
      if (!extraAttributes.includes(key)) {
        extraAttributes.push(key);
      }
    }

    return extraAttributes;
  }

  get objectType() {
    return this.store.peekRecord('object-type', this.args.objectTypeId);
  }

  get showCustomerRelationshipsData() {
    return this.args.customer != null;
  }

  get relationships() {
    return this.fetchRelationships.lastSuccessful?.value ?? [];
  }

  get isAllSelected() {
    return (this.totalRecs && this.totalRecs === this.selectedRelationships.length) || this.allSelected;
  }

  get allRelationshipsOnPageSelected() {
    if (this.selectedRelationships.length === 0 || this.relationships.length === 0) {
      return false;
    }

    if (this.isAllSelected && this.totalRecs > this.selectedRelationships.length) {
      return false;
    }

    return this.selectedRelationships.length === this.relationships.length;
  }

  get indeterminate() {
    if (this.isAllSelected && this.totalRecs > this.relationships.length) {
      return false;
    }

    return (this.selectedRelationships.length ?? 0) > 0 && !this.allRelationshipsOnPageSelected;
  }

  get blankSlateColSpan() {
    let baseAmountOfColumns = 3;
    // if no custom attribute columns are displayed, we display "attributes" column, which is why it is 1 extra
    let extraColumns =
      this.args.isCardView == null && this.relationshipTableColumns.length > 0
        ? this.relationshipTableColumns.length
        : 1;

    return baseAmountOfColumns + extraColumns;
  }

  @action
  changePage(page: number) {
    this.page = page;
    this.clearSelection();
    this.fetchRelationships.perform();
  }

  @action
  clearSelection() {
    this.allSelected = false;
    this.selectedRelationships = [];
  }

  @action
  selectAllRelationshipsOnPage(checked: boolean) {
    if (checked) {
      this.allSelected = this.relationships.length === this.totalRecs;
      this.selectedRelationships = this.relationships.slice();
    } else {
      this.clearSelection();
    }
  }

  @action
  selectAll() {
    this.allSelected = true;
    this.selectedRelationships = this.relationships.slice();
  }

  @action
  onRelationshipsAdd() {
    this.showRelationshipsModal = false;

    this.fetchRelationships.perform({ delay: config.APP.relationshipsChangedDelay });
  }

  deleteRelationships = dropTask(async (deleteAll = false) => {
    this.hideAttributeSidebar();
    let relationshipsToDelete =
      deleteAll === true
        ? await this.fetchRelationships.perform({
            itemsPerPage: this.totalRecs,
            page: 1,
          })
        : this.selectedRelationships;

    if (relationshipsToDelete == null) {
      this.alerts.error(
        'relationships.delete',
        'There was an error deleting all relationships, please try again later',
      );
      return;
    }

    let relationships = relationshipsToDelete.map(({ entity1Type, entity1Id, entity2Type, entity2Id }) => ({
      entity1_type: entity1Type,
      entity1_id: entity1Id,
      entity2_type: entity2Type,
      entity2_id: entity2Id,
    }));

    try {
      await this.api.delete(`relationships`, { data: { relationships } });
      this.snackbar.add('relationships.delete', `${relationships.length} Relationships deleted`);

      this.analytics.track('objects', 'relationships_manual_delete_completed', {
        source: this.args.source,
        peopleCount: this.args.object != null ? this.selectedRelationships.length : 1,
        objectCount: this.args.customer != null ? this.selectedRelationships.length : 1,
      });

      this.fetchRelationships.perform({ delay: config.APP.relationshipsChangedDelay });
      this.clearSelection();
    } catch {
      this.alerts.error('relationships.delete', 'There was an error deleting relationships, please try again later');
    }
  });

  @action
  entryChecked(
    relationship: RelationshipModel,
    index: number,
    checked: boolean,
    metadata: { event: FlyMouseEvent<HTMLInputElement> },
  ) {
    metadata.event.stopImmediatePropagation();

    let { tempShiftSelectEnd, tempShiftSelectStart, allSelected } = rangeSelectionHelper<RelationshipModel>({
      models: this.relationships,
      singleModel: relationship,
      selectedItems: this.selectedRelationships,
      index,
      checked,
      totalRecs: this.totalRecs,
      metadata,
      shiftSelectStart: this.shiftSelectStart,
      shiftSelectEnd: this.shiftSelectEnd,
    });

    this.shiftSelectStart = tempShiftSelectStart;
    this.shiftSelectEnd = tempShiftSelectEnd;
    this.allSelected = allSelected;
  }

  @action
  showColumnsModalAction() {
    this.showColumnsModal = true;
  }

  @action
  hideColumnsModal() {
    this.showColumnsModal = false;
  }

  @action
  setColumns(columns: string[]) {
    let relationshipsTableColumns = { [this.args.objectTypeId]: columns };
    let promise = this.settings.saveEnvironmentSetting.perform('relationshipColumns', relationshipsTableColumns);
    promise.then(() => (this.showColumnsModal = false), ignorePromiseFailure);
    return promise;
  }

  @action
  showAttributeModalAction(relationship: RelationshipModel) {
    let oldId = this.relationshipForModal?.id;

    this.relationshipForModal = relationship;
    this.setAttributesProxy(relationship);
    this.setEditMode(relationship.attrs.length === 0);

    if (oldId === relationship.id) {
      this.hideAttributeSidebar();
    } else {
      this.sidebar.open(this.sidebarId);
    }
  }

  @action
  setEditMode(editMode: boolean) {
    this.editingAttributesForm = editMode;
  }

  @action
  hideAttributeSidebar() {
    this.sidebar.close();
    this.relationshipForModal = null;
  }

  @action
  setAttributesProxy(relationship: RelationshipModel) {
    this.attributesProxy = AttributesProxy.create({
      attrs: relationship.attrs,
      requiredAttributes: [],
      autoGeneratedAttributes: [],
      isNew: false,
    });
  }

  fetchRelationships = restartableTask(async (options?: { delay?: number; itemsPerPage?: number; page?: number }) => {
    let { delay, itemsPerPage, page } = options ?? {};

    let model = this.args.object ?? this.args.customer;

    if (model == null) {
      return null;
    }

    let { relationships, meta } = await this.relationshipsService.fetchRelationships.perform({
      model,
      objectTypeId: this.args.objectTypeId,
      itemsPerPage: itemsPerPage ?? this.itemsPerPage,
      page: page ?? this.page,
      delay,
    });

    this.totalRecs = meta?.pagination?.total ?? -1;
    return relationships;
  });
}
